import React, { useState, useEffect } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import PlusMinQty from "../../utilities/PlusMinQty";
import { X } from "lucide-react";

const defSLTP = ["Price", "PNL"];

const LegRiskModal = ({ show, setShow, popUpData, info, onSave }) => {
  const [formData, setFormData] = useState({});
  const [errorMessages, setErrorMessages] = useState({
    stopLoss: "",
    target: "",
    trailSL: "",
    profitLock: "",
    profitLockTrail: ""
  });
  const [calcVal, setcalcVal] = useState(0);
  const [slValueType, setSlValueType] = useState(0);
  const [tpValueType, setTpValueType] = useState(0);
  const [trailValueType, setTrailValueType] = useState(2);

  useEffect(() => {
    if (popUpData) {
      const legRms = popUpData?.legRms || {};
      const normalizedFormData = {
        ...popUpData,
        legRms: {
          basis: legRms.basis ?? 0,
          takeProfitInput: legRms.targetInput || null,
          stopLossInput: legRms.stopLossInput || null,
          trailSL: legRms.trailSL || null,
          profitLockAt: legRms.profitLockAt || null,
          profitLockSl: legRms.profitLockSl || null,
          profitLockTrail: legRms.profitLockTrail || null,
        },
        sltpType: popUpData?.sltpType || (legRms.basis === 2 ? "Underlying" : (legRms.basis === 0 ? "PNL" : "Price")),
      };
      setFormData(normalizedFormData);
      const currentSltpType = normalizedFormData.sltpType || (legRms.basis === 0 ? "PNL" : "Price");
      const slType = legRms?.stopLossInput?.valueType;
      const tpType = legRms?.targetInput?.valueType;
      if (currentSltpType === "PNL") {
        setSlValueType(slType ?? 0);
        setTpValueType(tpType ?? 0);
      } else {
        setSlValueType(slType);
        setTpValueType(tpType);
      }
      setTrailValueType(legRms?.trailSL?.valueType ?? 2);
      if (popUpData.row && popUpData.row.data) {
        const data = popUpData.row.data;
        let calc = 0;
        for (let i = 0; i < data.length; i++) {
          if (data[i].netQuantity != 0) {
            calc += parseFloat(data[i].ltp || 0);
          }
        }
        setcalcVal(calc);
      }
    }
  }, [popUpData]);

  const handleReset = () => {
    if (popUpData) {
      const legRms = popUpData?.legRms || {};
      const resetFormData = {
        ...popUpData,
        legRms: {
          basis: legRms.basis ?? 0,
          takeProfitInput: null,
          stopLossInput: null,
          trailSL: null,
          profitLockAt: null,
          profitLockSl: null,
          profitLockTrail: null,
        },
        sltpType: popUpData?.sltpType || (legRms.basis === 2 ? "Underlying" : (legRms.basis === 0 ? "PNL" : "Price")),
      };
      setFormData(resetFormData);
      setErrorMessages({
        stopLoss: "",
        target: "",
        trailSL: "",
        profitLock: "",
        profitLockTrail: ""
      });
      const currentSltpType = resetFormData.sltpType || (legRms.basis === 0 ? "PNL" : "Price");
      if (currentSltpType === "PNL") {
        setSlValueType(0);
        setTpValueType(0);
      } else {
        setSlValueType(2);
        setTpValueType(2);
      }
      setTrailValueType(2);
    }
  };

  const cycleWithin = (current, allowed) => {
    const idx = allowed.indexOf(current);
    const nextIdx = (idx + 1) % allowed.length;
    return allowed[nextIdx];
  };

  const renderTypeLabel = (vt) => (vt === 0 ? "₹" : vt === 1 ? "%" : "Pts");
  const renderTrailTypeLabel = (vt) => (vt === 1 ? "%" : "Pts");
  const renderPNLLabel = () => "₹₹";
  const renderPNLSLTPLabel = (vt) => (vt === 0 ? "₹₹" : "Pts");

  const onClickSlType = () => {
    const basis = formData?.sltpType;
    const newSlValueType = basis === "PNL" ?
      cycleWithin(slValueType, [0, 2]) :
      cycleWithin(slValueType, [1, 2]);
    setSlValueType(newSlValueType);
    isValidsltp("sl", formData?.legRms?.stopLossInput?.value, newSlValueType);
  };

  const onClickTpType = () => {
    const basis = formData?.sltpType;
    const newTpValueType = basis === "PNL" ?
      cycleWithin(tpValueType, [0, 2]) :
      cycleWithin(tpValueType, [1, 2]);
    setTpValueType(newTpValueType);
    isValidsltp("tp", formData?.legRms?.takeProfitInput?.value, newTpValueType);
  };

  const onClickTrailType = () => {
    setTrailValueType(prev => cycleWithin(prev, [2, 1]));
  };

  const validateTrailSL = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, trailSL: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, trailSL: "Trail SL Reference and Step must be non-negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    return true;
  };

  const validateProfitLock = (profitLockAt, profitLockSl) => {
    const atVal = profitLockAt !== null && profitLockAt !== undefined && profitLockAt !== '' ? parseFloat(profitLockAt) : null;
    const slVal = profitLockSl !== null && profitLockSl !== undefined && profitLockSl !== '' ? parseFloat(profitLockSl) : null;

    if (atVal !== null && slVal === null) {
      setErrorMessages((prev) => ({ ...prev, profitLock: "Both fields are required" }));
      return false;
    }

    setErrorMessages((prev) => ({ ...prev, profitLock: "" }));
    return true;
  };

  const validateProfitLockTrail = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Both fields are required" }));
      return false;
    }

    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Profit Trail values cannot be negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    return true;
  };

  const setMsg = (type, ok, customMsg = null) => {
    if (type === "sl") {
      const msg = customMsg
      setErrorMessages((prev) => ({ ...prev, stopLoss: ok ? "" : msg }));
    } else {
      const msg = customMsg
      setErrorMessages((prev) => ({ ...prev, target: ok ? "" : msg }));
    }
  };

  const isValidsltp = (type, val, overrideType = null) => {
    console.log("isValidsltp", type, val, overrideType);
    if (val === null || val === undefined || val === '' || val === 0) {
      setMsg(type, true);
      return true;
    }
    const parsedVal = parseFloat(val);
    if (Number.isNaN(parsedVal)) {
      setMsg(type, true);
      return true;
    }

    const basisIsPNL = formData.sltpType === "PNL";
    if (!basisIsPNL) {
      if (parsedVal <= 0) {
        setMsg(type, false, type === "sl" ? "Stop Loss must be positive" : "Target must be positive");
        return false;
      }
    } else {
      const valueType = overrideType ?? (type === "sl" ? slValueType : tpValueType);
      if (type === "sl" && parsedVal < 0 && valueType == 2) {
        setMsg(type, false, "Stop Loss cannot be negative");
        return false;
      }
      if (type === "tp" && parsedVal < 0 && valueType == 2) {
        setMsg(type, false, "Target cannot be negative");
        return false;
      }
      if (type === "sl" && parsedVal > 0 && valueType !== 2) {
        setMsg(type, false, "Stop Loss cannot be positive");
        return false;
      }
      if (type === "tp" && parsedVal < 0 && valueType !== 2) {
        setMsg(type, false, "Target cannot be negative");
        return false;
      }
    }
    setMsg(type, true);
    return true;
  };

  useEffect(() => {
    if (!show || !popUpData || !formData || Object.keys(formData).length === 0) return;

    const stopLossValue = formData?.legRms?.stopLossInput?.value;
    if (stopLossValue !== null && stopLossValue !== undefined && stopLossValue !== '' && stopLossValue !== 0) {
      isValidsltp("sl", stopLossValue);
    }

    const targetValue = formData?.legRms?.takeProfitInput?.value;
    if (targetValue !== null && targetValue !== undefined && targetValue !== '' && targetValue !== 0) {
      isValidsltp("tp", targetValue);
    }

    validateTrailSL(formData?.legRms?.trailSL?.reference, formData?.legRms?.trailSL?.step);

    validateProfitLock(formData?.legRms?.profitLockAt, formData?.legRms?.profitLockSl);

    validateProfitLockTrail(formData?.legRms?.profitLockTrail?.reference, formData?.legRms?.profitLockTrail?.step);
  }, []);

  const handleFieldChange = (val, name, headersValue) => {
    if (name !== "sltpType") {
      const parsed = (!val && val !== 0) ? null : parseFloat(val);
      const updated = { ...formData, legRms: { ...(formData.legRms || {}) } };


      if (name === "stopLossAbsolute") {
        if (parsed === null || isNaN(parsed)) {
          updated.legRms.stopLossInput = null;
          updated.legRms.trailSL = null;
          setErrorMessages((prev) => ({ ...prev, stopLoss: "", trailSL: "" }));
        } else {
          updated.legRms.stopLossInput = {
            valueType: formData?.legRms?.stopLossInput?.valueType ?? slValueType,
            value: parsed
          };
          isValidsltp("sl", parsed);
        }
      } else if (name === "takeProfitAbsolute") {
        if (parsed === null || isNaN(parsed)) {
          updated.legRms.takeProfitInput = null;
          setErrorMessages((prev) => ({ ...prev, target: "" }));
        } else {
          updated.legRms.takeProfitInput = {
            valueType: formData?.legRms?.takeProfitInput?.valueType ?? tpValueType,
            value: parsed
          };
          isValidsltp("tp", parsed);
        }
      } else if (name === "trailReferenceAbsolute" || name === "trailStepAbsolute") {
        const base = { ...(formData?.legRms?.trailSL || {}) };
        if (name === "trailReferenceAbsolute") base.reference = parsed;
        if (name === "trailStepAbsolute") base.step = parsed;
        if (base.reference == null && base.step == null) {
          updated.legRms.trailSL = null;
          setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
        } else {
          updated.legRms.trailSL = {
            valueType: formData?.legRms?.trailSL?.valueType ?? trailValueType,
            reference: base.reference ?? null,
            step: base.step ?? null
          };
          // Validate after updating the structure (don't return early - allow state update)
          validateTrailSL(updated.legRms.trailSL.reference, updated.legRms.trailSL.step);
        }
      } else if (name === "profitLockAt" || name === "profitLockSl") {
        updated.legRms[name] = parsed;
        if (parsed === null) {
          updated.legRms.profitLockTrail = null;
          setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
        }
        const newAt = name === "profitLockAt" ? updated.legRms.profitLockAt : formData.legRms?.profitLockAt;
        const newSl = name === "profitLockSl" ? updated.legRms.profitLockSl : formData.legRms?.profitLockSl;
        validateProfitLock(newAt, newSl);
      } else if (name === "profitLockTrailRef" || name === "profitLockTrailStep") {
        const base = { ...(formData?.legRms?.profitLockTrail || {}) };
        if (name === "profitLockTrailRef") base.reference = parsed;
        if (name === "profitLockTrailStep") base.step = parsed;
        if (base.reference == null && base.step == null) {
          updated.legRms.profitLockTrail = null;
          setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
        } else {
          updated.legRms.profitLockTrail = {
            valueType: 2,
            reference: base.reference ?? null,
            step: base.step ?? null
          };
          validateProfitLockTrail(base.reference, base.step);
        }
      }
      setFormData(updated);
    } else {
      const updated = { ...formData, sltpType: val };
      if (val === "PNL") {
        setSlValueType(0);
        setTpValueType(0);
      } else {
        setSlValueType(2);
        setTpValueType(2);
      }
      setTrailValueType(2);
      setErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });
      setFormData(updated);
    }
  };

  const clickActionSave = () => {
    const stopLossValue = formData?.legRms?.stopLossInput?.value;
    if (stopLossValue !== null && stopLossValue !== undefined && stopLossValue !== '' && stopLossValue !== 0) {
      if (!isValidsltp("sl", stopLossValue)) {
        return;
      }
    }

    const targetValue = formData?.legRms?.takeProfitInput?.value;
    if (targetValue !== null && targetValue !== undefined && targetValue !== '' && targetValue !== 0) {
      if (!isValidsltp("tp", targetValue)) {
        console.log("tp", targetValue);
        return;
      }
    }

    const trailRef = formData?.legRms?.trailSL?.reference;
    const trailStep = formData?.legRms?.trailSL?.step;
    if (!validateTrailSL(trailRef, trailStep)) {
      return;
    }

    if (!validateProfitLock(formData?.legRms?.profitLockAt, formData?.legRms?.profitLockSl)) {
      return;
    }

    const profitTrailRef = formData?.legRms?.profitLockTrail?.reference;
    const profitTrailStep = formData?.legRms?.profitLockTrail?.step;
    if (!validateProfitLockTrail(profitTrailRef, profitTrailStep)) {
      return;
    }

    const basisIsPnl = (formData?.sltpType === "PNL");
    const norm = (n) => {
      if (n === 0) return 0;
      if (n === undefined || n === null || n === "") return null;
      const p = parseFloat(n);
      return Number.isNaN(p) ? null : p;
    };
    const lr = formData.legRms || {};
    const updatedLegRms = {
      basis: basisIsPnl ? 0 : 1,
      targetInput: lr.takeProfitInput && norm(lr.takeProfitInput.value) !== null ? {
        valueType: tpValueType ?? 2,
        value: norm(lr.takeProfitInput.value)
      } : null,
      stopLossInput: lr.stopLossInput && norm(lr.stopLossInput.value) !== null ? {
        valueType: slValueType ?? 2,
        value: norm(lr.stopLossInput.value)
      } : null,
      trailSL: lr.trailSL && (norm(lr.trailSL.reference) !== null || norm(lr.trailSL.step) !== null) ? {
        valueType: (trailValueType === 1 ? 1 : 2),
        reference: norm(lr.trailSL.reference),
        step: norm(lr.trailSL.step)
      } : null,
      profitLockAt: norm(lr.profitLockAt),
      profitLockSl: norm(lr.profitLockSl),
      profitLockTrail: lr.profitLockTrail && (norm(lr.profitLockTrail.reference) !== null || norm(lr.profitLockTrail.step) !== null) ? {
        valueType: 2,
        reference: norm(lr.profitLockTrail.reference),
        step: norm(lr.profitLockTrail.step)
      } : null
    };
    const value = { ...formData, legRms: updatedLegRms };
    onSave({ formData: value });
  };

  const clearfield = (name) => {
    const updated = { ...formData, legRms: { ...(formData.legRms || {}) } };

    if (name === "stopLossAbsolute") {
      updated.legRms.stopLossInput = null;
      updated.legRms.trailSL = null;
      setErrorMessages((prev) => ({ ...prev, stopLoss: "" }));
    } else if (name === "takeProfitAbsolute") {
      updated.legRms.takeProfitInput = null;
      setErrorMessages((prev) => ({ ...prev, target: "" }));
    } else if (name === "trailReferenceAbsolute") {
      if (updated.legRms.trailSL) {
        updated.legRms.trailSL = {
          ...updated.legRms.trailSL,
          reference: null
        };
        if (updated.legRms.trailSL.step === null) {
          updated.legRms.trailSL = null;
          setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
        } else {
          // Validate after clearing reference - one field is null, other has value
          validateTrailSL(null, updated.legRms.trailSL.step);
        }
      }
    } else if (name === "trailStepAbsolute") {
      if (updated.legRms.trailSL) {
        updated.legRms.trailSL = {
          ...updated.legRms.trailSL,
          step: null
        };
        if (updated.legRms.trailSL.reference === null) {
          updated.legRms.trailSL = null;
          setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
        } else {
          // Validate after clearing step - one field is null, other has value
          validateTrailSL(updated.legRms.trailSL.reference, null);
        }
      }
    } else if (name === "profitLockAt") {
      updated.legRms.profitLockAt = null;
      updated.legRms.profitLockTrail = null;
    } else if (name === "profitLockSl") {
      updated.legRms.profitLockSl = null;
      updated.legRms.profitLockTrail = null;
    } else if (name === "profitLockTrailRef") {
      const otherValue = formData?.legRms?.profitLockTrail?.step;
      if (updated.legRms.profitLockTrail) {
        updated.legRms.profitLockTrail = {
          ...updated.legRms.profitLockTrail,
          reference: null
        };
        if (updated.legRms.profitLockTrail.step === null) {
          updated.legRms.profitLockTrail = null;
          setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
        } else {
          // Validate after clearing reference - one field is null, other has value
          validateProfitLockTrail(null, updated.legRms.profitLockTrail.step);
        }
      } else if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        // If structure doesn't exist but other field has value, validate
        validateProfitLockTrail(null, otherValue);
      }
    } else if (name === "profitLockTrailStep") {
      const otherValue = formData?.legRms?.profitLockTrail?.reference;
      if (updated.legRms.profitLockTrail) {
        updated.legRms.profitLockTrail = {
          ...updated.legRms.profitLockTrail,
          step: null
        };
        if (updated.legRms.profitLockTrail.reference === null) {
          updated.legRms.profitLockTrail = null;
          setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
        } else {
          // Validate after clearing step - one field is null, other has value
          validateProfitLockTrail(updated.legRms.profitLockTrail.reference, null);
        }
      } else if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        // If structure doesn't exist but other field has value, validate
        validateProfitLockTrail(otherValue, null);
      }
    }

    setFormData(updated);
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} size="sm" centered className="fixed-modal-sltp ">
      <Modal.Header style={{ position: 'relative' }}>
        <Modal.Title style={{ minWidth: 0 }} className="modal-title-size" id="contained-modal-title-vcenter">
          Default Leg Risk Settings
        </Modal.Title>
        <button type="button" className="modal-close-button" onClick={() => setShow(false)} aria-label="Close">×</button>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-sltp">
          <div style={{ justifyContent: "space-between" }} className="sltp-div modal-input ">
            <Form.Group className="form-group">
              <label className="form-label">SL/TP Basis</label>
              <Form.Select
                aria-label="Default select example"
                name="sltpType"
                value={formData.sltpType}
                onChange={(e) => handleFieldChange(e.target.value, "sltpType", popUpData?.headersValue)}
              >
                {defSLTP.map((data, index) => (
                  <option value={data} key={index}>{data}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          {(
            <div className={"sltp-div"}>
              <div className="sltp-col-left">
                <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>Stop Loss</Form.Label>
                <div className="sltp-input-row">
                  <div className={errorMessages.stopLoss ? "error-border" : ""}>
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "stopLossAbsolute", popUpData.headersValue)}
                      filterValue={formData?.legRms?.stopLossInput?.value ?? null}
                      name={"stopLossAbsolute"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                  </div>
                  {formData?.sltpType === "PNL" ? (
                    <div className="pts-class" onClick={onClickSlType}>{renderPNLSLTPLabel(slValueType)}</div>
                  ) : (
                    <div className="pts-class" onClick={onClickSlType}>{renderTypeLabel(slValueType)}</div>
                  )}
                  <X size={16} onClick={() => clearfield("stopLossAbsolute")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                </div>
                {errorMessages.stopLoss && (<div className="text-danger mt-1" style={{ fontSize: '10px' }}>{errorMessages.stopLoss}</div>)}
              </div>
              <div className="sltp-col-right">
                <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>Target</Form.Label>
                <div className="sltp-input-row">
                  <div className={errorMessages.target ? "error-border" : ""}>
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "takeProfitAbsolute", popUpData.headersValue)}
                      filterValue={formData?.legRms?.takeProfitInput?.value ?? null}
                      name={"takeProfitAbsolute"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                  </div>
                  {formData?.sltpType === "PNL" ? (
                    <div className="pts-class" onClick={onClickTpType}>{renderPNLSLTPLabel(tpValueType)}</div>
                  ) : (
                    <div className="pts-class" onClick={onClickTpType}>{renderTypeLabel(tpValueType)}</div>
                  )}
                  <X size={16} onClick={() => clearfield("takeProfitAbsolute")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                </div>
                {errorMessages.target && (<div className="text-danger mt-1" style={{ fontSize: '10px' }}>{errorMessages.target}</div>)}
              </div>
            </div>
          )}

          {(
            <div className="sltp-div-profile">
              <p className="sltp-trail-header">Stoploss Trailing</p>
              <div className="sltp-div">
                <div className="sltp-col-left">
                  <Form.Label className="sltp-label-above">For every X move</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "trailReferenceAbsolute", popUpData.headersValue)}
                      filterValue={formData?.legRms?.trailSL?.reference ?? null}
                      name={"trailReferenceAbsolute"}
                      min={0.05}
                      step={0.05}
                      disabled={!formData?.legRms?.stopLossInput || formData?.legRms?.stopLossInput?.value === null}
                      formatINR={true}
                    />
                    {formData?.sltpType === "PNL" ? (
                      <div className="pts-class">{renderPNLLabel()}</div>
                    ) : (
                      <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                    )}
                    <X size={16} onClick={() => clearfield("trailReferenceAbsolute")} className={`sltp-delete-icon ${((formData?.legRms?.stopLossInput?.value ?? null) === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} style={{ cursor: 'pointer' }} />
                  </div>
                </div>
                <div className="sltp-col-right">
                  <Form.Label className="sltp-label-above">Trail SL by Y</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "trailStepAbsolute", popUpData.headersValue)}
                      filterValue={formData?.legRms?.trailSL?.step ?? null}
                      name={"trailStepAbsolute"}
                      min={0.05}
                      step={0.05}
                      disabled={!formData?.legRms?.stopLossInput || formData?.legRms?.stopLossInput?.value === null}
                      formatINR={true}
                    />
                    {formData?.sltpType === "PNL" ? (
                      <div className="pts-class">{renderPNLLabel()}</div>
                    ) : (
                      <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                    )}
                    <X size={16} onClick={() => clearfield("trailStepAbsolute")} className={`sltp-delete-icon ${((formData?.legRms?.stopLossInput?.value ?? null) === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} style={{ cursor: 'pointer' }} />
                  </div>
                </div>
              </div>
              {errorMessages.trailSL && (
                <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                  {errorMessages.trailSL}
                </div>
              )}
              <p className="sltp-trail-header">Profit Locking</p>
              <div className="sltp-div">
                <div className="sltp-col-left">
                  <Form.Label className="sltp-label-above">When profit reaches</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "profitLockAt", popUpData.headersValue)}
                      filterValue={formData?.legRms?.profitLockAt || null}
                      name={"profitLockAt"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X size={16} onClick={() => clearfield("profitLockAt")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                  </div>
                </div>
                <div className="sltp-col-right">
                  <Form.Label className="sltp-label-above">Lock minimum profit at</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "profitLockSl", popUpData.headersValue)}
                      filterValue={formData?.legRms?.profitLockSl || null}
                      name={"profitLockSl"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X size={16} onClick={() => clearfield("profitLockSl")} className="sltp-delete-icon sltp-delete-icon-enabled" style={{ cursor: 'pointer' }} />
                  </div>
                </div>
              </div>
              {errorMessages.profitLock && (
                <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                  {errorMessages.profitLock}
                </div>
              )}

              <p className="sltp-trail-header">Profit Trailing</p>
              <div className="sltp-div">
                <div className="sltp-col-left">
                  <Form.Label className="sltp-label-above">If profit increases by X</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "profitLockTrailRef", popUpData.headersValue)}
                      filterValue={formData?.legRms?.profitLockTrail?.reference || null}
                      name={"profitLockTrailRef"}
                      min={0.05}
                      step={0.05}
                      max={null}
                      title={""}
                      disabled={formData?.legRms?.profitLockAt === null || formData?.legRms?.profitLockSl === null}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X size={16} onClick={() => clearfield("profitLockTrailRef")} className={`sltp-delete-icon ${(formData?.legRms?.profitLockAt === null || formData?.legRms?.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} style={{ cursor: 'pointer' }} />
                  </div>
                </div>
                <div className="sltp-col-right">
                  <Form.Label className="sltp-label-above">Trail Profit by Y</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty) => handleFieldChange(qty, "profitLockTrailStep", popUpData.headersValue)}
                      filterValue={formData?.legRms?.profitLockTrail?.step || null}
                      name={"profitLockTrailStep"}
                      min={0.05}
                      step={0.05}
                      max={null}
                      title={""}
                      disabled={formData?.legRms?.profitLockAt === null || formData?.legRms?.profitLockSl === null}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X size={16} onClick={() => clearfield("profitLockTrailStep")} className={`sltp-delete-icon ${(formData?.legRms?.profitLockAt === null || formData?.legRms?.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`} style={{ cursor: 'pointer' }} />
                  </div>
                </div>
              </div>
              {errorMessages.profitLockTrail && (
                <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                  {errorMessages.profitLockTrail}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Summary Section */}
        <div className="sltp-summary-section">
          <div className="sltp-summary-title">
            Summary
          </div>
          <div className="sltp-summary-body">
            {(() => {
              const summaryLines = [];
              const basisLabel = formData?.sltpType === "PNL" ? "PnL" : "price";

              // Stop Loss
              const slValue = formData?.legRms?.stopLossInput?.value;
              if (slValue !== null && slValue !== undefined && slValue !== '' && slValue !== 0) {
                const parsedSl = parseFloat(slValue);
                if (formData?.sltpType === "PNL") {
                  if (slValueType === 0) {
                    summaryLines.push(`If ${basisLabel} falls to ₹${Math.abs(parsedSl).toFixed(2)}, the strategy will exit.`);
                  } else {
                    summaryLines.push(`If ${basisLabel} falls by ${Math.abs(parsedSl).toFixed(2)} pts, the strategy will exit.`);
                  }
                } else {
                  if (slValueType === 1) {
                    summaryLines.push(`If ${basisLabel} moves by ${parsedSl.toFixed(2)}%, the strategy will exit.`);
                  } else {
                    summaryLines.push(`If ${basisLabel} moves by ${parsedSl.toFixed(2)} pts, the strategy will exit.`);
                  }
                }
              }

              // Target
              const tpValue = formData?.legRms?.takeProfitInput?.value;
              if (tpValue !== null && tpValue !== undefined && tpValue !== '' && tpValue !== 0) {
                const parsedTp = parseFloat(tpValue);
                if (formData?.sltpType === "PNL") {
                  if (tpValueType === 0) {
                    summaryLines.push(`If ${basisLabel} touches ₹${Math.abs(parsedTp).toFixed(2)}, the strategy will exit with profit.`);
                  } else {
                    summaryLines.push(`If ${basisLabel} moves up by ${Math.abs(parsedTp).toFixed(2)} pts, the strategy will exit with profit.`);
                  }
                } else {
                  if (tpValueType === 1) {
                    summaryLines.push(`If ${basisLabel} moves by ${parsedTp.toFixed(2)}%, the strategy will exit with profit.`);
                  } else {
                    summaryLines.push(`If ${basisLabel} moves by ${parsedTp.toFixed(2)} pts, the strategy will exit with profit.`);
                  }
                }
              }

              // Stoploss Trailing
              const trailRef = formData?.legRms?.trailSL?.reference;
              const trailStep = formData?.legRms?.trailSL?.step;
              if ((trailRef !== null && trailRef !== undefined && trailRef !== '') &&
                (trailStep !== null && trailStep !== undefined && trailStep !== '')) {
                const parsedRef = parseFloat(trailRef);
                const parsedStep = parseFloat(trailStep);
                if (formData?.sltpType === "PNL") {
                  summaryLines.push(`For every ₹${parsedRef.toFixed(2)} increase in profit, SL will move up by ₹${parsedStep.toFixed(2)}.`);
                } else {
                  if (trailValueType === 1) {
                    summaryLines.push(`For every ${parsedRef.toFixed(2)}% increase in profit, SL will move up by ${parsedStep.toFixed(2)}%.`);
                  } else {
                    summaryLines.push(`For every ${parsedRef.toFixed(2)} pts increase in profit, SL will move up by ${parsedStep.toFixed(2)} pts.`);
                  }
                }
              }

              // Profit Locking
              const profitAt = formData?.legRms?.profitLockAt;
              const profitSl = formData?.legRms?.profitLockSl;
              if ((profitAt !== null && profitAt !== undefined && profitAt !== '') &&
                (profitSl !== null && profitSl !== undefined && profitSl !== '')) {
                const parsedAt = parseFloat(profitAt);
                const parsedSl = parseFloat(profitSl);
                summaryLines.push(`When profit reaches ₹${parsedAt.toFixed(2)}, at least ₹${parsedSl.toFixed(2)} will be protected.`);
              }

              // Profit Trailing
              const profitTrailRef = formData?.legRms?.profitLockTrail?.reference;
              const profitTrailStep = formData?.legRms?.profitLockTrail?.step;
              if ((profitTrailRef !== null && profitTrailRef !== undefined && profitTrailRef !== '') &&
                (profitTrailStep !== null && profitTrailStep !== undefined && profitTrailStep !== '')) {
                const parsedRef = parseFloat(profitTrailRef);
                const parsedStep = parseFloat(profitTrailStep);
                summaryLines.push(`Each time profit increases by ₹${parsedRef.toFixed(2)}, locked profit will rise by ₹${parsedStep.toFixed(2)}.`);
              }

              if (summaryLines.length === 0) {
                return <div style={{ color: '#6b7785', fontStyle: 'italic' }}>No risk settings configured</div>;
              }

              return summaryLines.map((line, index) => (
                <div key={index} style={{ marginBottom: index < summaryLines.length - 1 ? '8px' : '0' }}>
                  {line}
                </div>
              ));
            })()}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleReset} variant="outline-primary">Reset</Button>
        <Button disabled={!!errorMessages.stopLoss || !!errorMessages.target || !!errorMessages.trailSL || !!errorMessages.profitLock || !!errorMessages.profitLockTrail} onClick={() => { clickActionSave(); setShow(false); }}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LegRiskModal;